import React, { useRef, useEffect, useMemo } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { useTexture } from '@react-three/drei'
import * as THREE from 'three'
import aboutDataJson from './aboutData.json'

// use srgb colorspace for textures
THREE.Texture.DEFAULT_COLOR_SPACE = THREE.SRGBColorSpace
THREE.WebGLRenderer.prototype.outputEncoding = THREE.sRGBEncoding

/**
 * Spacing Constants
 * Used consistently for both position calculation and rendering
 */
const STEP_SPACING = 2.5 // Distance between steps
const SECTION_SPACING = 24 // Distance between sections

/**
 * aboutOverlay Component
 * 
 * Main 3D scene for the about section
 * Renders content in a horizontal scrolling layout similar to workOverlay
 * Structure: ONE phase with 4 sections (Introduction, Experience, Skills, Achievements)
 */

const aboutOverlay = ({
  currentItemIndex,
  onNavigate
}) => {
  const { camera } = useThree()
  const sceneGroupRef = useRef()
  const targetScenePosition = useRef(new THREE.Vector3(0, 0, 0))
  const currentScenePosition = useRef(new THREE.Vector3(0, 0, 0))
  const animationProgress = useRef(1)
  
  // Detect mobile viewport
  const [isMobile, setIsMobile] = React.useState(false)
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Set camera to look at the scene (forward facing)
  useEffect(() => {
    camera.lookAt(0, 0, 0)
  }, [camera])

  // Pre-calculate all step positions (Y-axis for top-to-bottom scrolling)
  const allStepPositions = useMemo(() => {
    const sections = aboutDataJson['about-phase']?.sections || []
    
    const positions = []
    let cumulativePosition = 0
    
    sections.forEach(section => {
      section.subsections?.forEach(subsection => {
        subsection.steps.forEach((step, stepIndex) => {
          const pos = cumulativePosition + (stepIndex * STEP_SPACING)
          positions.push(pos)
        })
        cumulativePosition += (subsection.steps.length - 1) * STEP_SPACING + SECTION_SPACING
      })
    })
    
    return positions
  }, [])

  // Calculate target scene position (Y-axis for vertical scrolling)
  useEffect(() => {
    const position = allStepPositions[currentItemIndex] || 0
    
    if (sceneGroupRef.current) {
      targetScenePosition.current.set(0, -position, 0)
    }
  }, [currentItemIndex, allStepPositions])
  
  // Initialize position on first mount (Y-axis for vertical)
  useEffect(() => {
    if (sceneGroupRef.current && allStepPositions.length > 0) {
      const position = allStepPositions[0] || 0
      currentScenePosition.current.set(0, -position, 0)
      sceneGroupRef.current.position.set(0, -position, 0)
      targetScenePosition.current.set(0, -position, 0)
      animationProgress.current = 1
    }
  }, [])

  // Smooth scene animation
  useFrame((state, delta) => {
    if (sceneGroupRef.current) {
      const lerpFactor = Math.min(delta * 8, 1)
      currentScenePosition.current.lerp(targetScenePosition.current, lerpFactor)
      sceneGroupRef.current.position.copy(currentScenePosition.current)
    }
  })

  return (
    <>
      {/* Hemisphere light creates gradient background */}
      <hemisphereLight 
        skyColor="#4b6cb7" 
        groundColor="#182848" 
        intensity={2}
      />

      {/* Scene group that moves instead of camera */}
      <group ref={sceneGroupRef}>
        <UnifiedLevelRenderer 
          currentItemIndex={currentItemIndex}
          isMobile={isMobile}
        />
      </group>
    </>
  )
}

/**
 * Unified Level Renderer
 * Renders ALL content for all sections
 */
const UnifiedLevelRenderer = ({ currentItemIndex, isMobile }) => {
  const [, forceUpdate] = React.useReducer(x => x + 1, 0)
  
  // Force re-render immediately on mount
  useEffect(() => {
    forceUpdate()
  }, [])
  
  const sections = aboutDataJson['about-phase']?.sections || []
  
  let cumulativePosition = 0
  const allContent = []
  
  sections.forEach((section, sectionIndex) => {
    section.subsections?.forEach((subsection) => {
      subsection.steps.forEach((step, stepIndex) => {
        const position = cumulativePosition + (stepIndex * STEP_SPACING)
        
        allContent.push(
          <group key={`${subsection.id}-${stepIndex}`} position={[0, position, 0]}>
            {step.type === 'graphic' && (
              <ProjectImageStep 
                step={step}
                isMobile={isMobile}
              />
            )}
          </group>
        )
      })
      
      cumulativePosition += (subsection.steps.length - 1) * STEP_SPACING + SECTION_SPACING
    })
  })
  
  return <>{allContent}</>
}

/**
 * Image Step - displays image using THREE.js mesh
 */
const ProjectImageStep = ({ step, isMobile }) => {
  const texture = useTexture(step.image)
  const { size } = useThree()
  
  // Dynamic frame dimensions based on actual window size (match workOverlay)
  const aspectRatio = 3 / 2  // 3:2 landscape aspect ratio
  const windowWidth = size.width
  
  let frameWidth
  if (windowWidth < 480) {
    // Mobile phones: smaller images for phones like Pixel, iPhone
    frameWidth = 2.2
  } else if (windowWidth < 768) {
    // Small tablets: small-medium images
    frameWidth = 2.5
  } else if (windowWidth < 1440) {
    // Tablet/small desktop: medium images (3-4 units)
    frameWidth = 3
  } else {
    // Large desktop: larger images (4-5 units)
    frameWidth = 3.5
  }
  
  const frameHeight = frameWidth / aspectRatio
  const frameAspect = aspectRatio
  
  // Calculate image aspect ratio
  const imageAspect = texture?.image ? texture.image.width / texture.image.height : frameAspect
  
  // Calculate texture scale for "contain" behavior - FIT INSIDE without cropping
  let scaleX = 1
  let scaleY = 1
  
  if (imageAspect > frameAspect) {
    // Image is wider - scale DOWN vertically to fit
    scaleX = frameAspect / imageAspect
  } else {
    // Image is taller - scale DOWN horizontally to fit
    scaleY = imageAspect / frameAspect
  }
  
  if (texture) {
    texture.repeat.set(scaleX, scaleY)
    texture.offset.set((1 - scaleX) / 2, (1 - scaleY) / 2)
  }
  
  // Position adjustments based on window size (match workOverlay)
  let xPosition = 0
  let yPosition = 0
  
  if (windowWidth < 480) {
    // Mobile phone positioning
    xPosition = 0
    yPosition = -0.75 // Lower on phone screens to give space for intro content
  } else if (windowWidth < 768) {
    // Small tablet positioning
    xPosition = 0
    yPosition = -0.5 // Lower on tablets
  } else if (windowWidth < 1440) {
    // Tablet/small desktop positioning
    xPosition = 1.0
    yPosition = 0
  } else {
    // Large desktop positioning
    xPosition = 1.25
    yPosition = 0
  }
  
  // Z position for depth
  const zPosition = 10
  
  return (
    <mesh 
      position={[xPosition, yPosition, zPosition]} 
      rotation={[0, 0, 0]}
    >
      <planeGeometry args={[frameWidth, frameHeight]} />
      {texture ? (
        <meshBasicMaterial map={texture} transparent side={THREE.DoubleSide} />
      ) : (
        <meshBasicMaterial color="#1a1a2e" transparent opacity={0.5} side={THREE.DoubleSide} />
      )}
    </mesh>
  )
}

export default aboutOverlay
